
import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../base/Button';
import LanguageSwitcher from '../base/LanguageSwitcher';

export default function Header() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const isHome = location.pathname === '/';
  const showSolidHeader = !isHome || isScrolled;

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleSectionNavigation = (sectionId: string) => {
    setIsMenuOpen(false);
    if (isHome) {
      scrollToSection(sectionId);
      return;
    }
    navigate(`/#${sectionId}`);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      showSolidHeader 
        ? 'bg-white shadow-lg' 
        : 'bg-transparent'
    }`}>
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center" onClick={() => setIsMenuOpen(false)}>
            <img 
              src={showSolidHeader ? "/assets/FlexyGroup.png" : "/assets/FlexyGroup-4.png"} 
              alt="Flexy Group Logo" 
              className="h-16 w-auto transition-all duration-300"
            />
          </Link>
          
          <nav className="hidden md:flex space-x-8">
            <button
              onClick={() => handleSectionNavigation('about')}
              className={`transition-colors cursor-pointer ${
                showSolidHeader 
                  ? 'text-gray-700 hover:text-[#9F7B5B]' 
                  : 'text-white hover:text-[#9F7B5B]'
              }`}
            >
              {t('nav.about')}
            </button>
            <button
              onClick={() => handleSectionNavigation('services')}
              className={`transition-colors cursor-pointer ${
                showSolidHeader 
                  ? 'text-gray-700 hover:text-[#9F7B5B]' 
                  : 'text-white hover:text-[#9F7B5B]'
              }`}
            >
              {t('nav.services')}
            </button>
            <button
              onClick={() => handleSectionNavigation('team')}
              className={`transition-colors cursor-pointer ${
                showSolidHeader 
                  ? 'text-gray-700 hover:text-[#9F7B5B]' 
                  : 'text-white hover:text-[#9F7B5B]'
              }`}
            >
              {t('nav.team')}
            </button>
            <button
              onClick={() => handleSectionNavigation('investment')}
              className={`transition-colors cursor-pointer ${
                showSolidHeader 
                  ? 'text-gray-700 hover:text-[#9F7B5B]' 
                  : 'text-white hover:text-[#9F7B5B]'
              }`}
            >
              {t('nav.investment')}
            </button>
            <Link
              to="/careers"
              className={`transition-colors cursor-pointer ${
                showSolidHeader 
                  ? 'text-gray-700 hover:text-[#9F7B5B]' 
                  : 'text-white hover:text-[#9F7B5B]'
              }`}
            >
              {t('nav.careers')}
            </Link>
            <button
              onClick={() => handleSectionNavigation('contact')}
              className={`transition-colors cursor-pointer ${
                showSolidHeader 
                  ? 'text-gray-700 hover:text-[#9F7B5B]' 
                  : 'text-white hover:text-[#9F7B5B]'
              }`}
            >
              {t('nav.contact')}
            </button>
          </nav>

          <div className="flex items-center space-x-4">
            <LanguageSwitcher />
            <button
              className={`md:hidden cursor-pointer transition-colors ${
                showSolidHeader 
                  ? 'text-gray-700 hover:text-[#9F7B5B]' 
                  : 'text-white hover:text-[#9F7B5B]'
              }`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <i className="ri-menu-line text-2xl"></i>
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div className={`md:hidden mt-4 pb-4 border-t transition-colors ${
            showSolidHeader ? 'border-gray-200' : 'border-white/20'
          }`}>
            <nav className="flex flex-col space-y-4 pt-4">
              <Link
                to="/"
                onClick={() => setIsMenuOpen(false)}
                className={`transition-colors text-left cursor-pointer ${
                  showSolidHeader 
                    ? 'text-gray-700 hover:text-[#9F7B5B]' 
                    : 'text-white hover:text-[#9F7B5B]'
                }`}
              >
                Home
              </Link>
              <button
                onClick={() => handleSectionNavigation('about')}
                className={`transition-colors text-left cursor-pointer ${
                  showSolidHeader 
                    ? 'text-gray-700 hover:text-[#9F7B5B]' 
                    : 'text-white hover:text-[#9F7B5B]'
                }`}
              >
                {t('nav.about')}
              </button>
              <button
                onClick={() => handleSectionNavigation('services')}
                className={`transition-colors text-left cursor-pointer ${
                  showSolidHeader 
                    ? 'text-gray-700 hover:text-[#9F7B5B]' 
                    : 'text-white hover:text-[#9F7B5B]'
                }`}
              >
                {t('nav.services')}
              </button>
              <button
                onClick={() => handleSectionNavigation('team')}
                className={`transition-colors text-left cursor-pointer ${
                  showSolidHeader 
                    ? 'text-gray-700 hover:text-[#9F7B5B]' 
                    : 'text-white hover:text-[#9F7B5B]'
                }`}
              >
                {t('nav.team')}
              </button>
              <button
                onClick={() => handleSectionNavigation('investment')}
                className={`transition-colors text-left cursor-pointer ${
                  showSolidHeader 
                    ? 'text-gray-700 hover:text-[#9F7B5B]' 
                    : 'text-white hover:text-[#9F7B5B]'
                }`}
              >
                {t('nav.investment')}
              </button>
              <Link
                to="/careers"
                onClick={() => setIsMenuOpen(false)}
                className={`transition-colors text-left cursor-pointer ${
                  showSolidHeader 
                    ? 'text-gray-700 hover:text-[#9F7B5B]' 
                    : 'text-white hover:text-[#9F7B5B]'
                }`}
              >
                {t('nav.careers')}
              </Link>
              <button
                onClick={() => handleSectionNavigation('contact')}
                className={`transition-colors text-left cursor-pointer ${
                  showSolidHeader 
                    ? 'text-gray-700 hover:text-[#9F7B5B]' 
                    : 'text-white hover:text-[#9F7B5B]'
                }`}
              >
                {t('nav.contact')}
              </button>
              <Button onClick={() => handleSectionNavigation('contact')} size="sm" className="w-fit">
                {t('nav.getStarted')}
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
